import React, { useMemo, useState } from "react";
import { Alert, Button, Card, DatePicker, Empty, Input, Modal, Radio, Segmented, Space, Table, Tag, Typography, message } from "antd";
import { PhoneOutlined, ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/nl_NL";
import urls from "urls";
import Loading from "components/shared-components/Loading";
import PickDayStepsBar from "components/shared-components/PickDay/PickDayStepsBar";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import { usePickDayOrders, usePickers, useProducts, useShortageHandling } from "data/hooks";
import { buildProductIndex } from "functions/pickLines";
import { findShortages, isShortageOpen, SHORTAGE_OUTCOMES, summariseShortages } from "functions/shortages";
import { toDayjs, todayPickDay } from "../shared";

const OUTCOME_COLORS = { unreachable: "orange", later: "blue", refund: "purple", replacement: "green" };

const customerName = (order) => {
  const customer = order.customer || {};
  const delivery = (customer.address && customer.address.delivery) || {};
  return [customer.firstName || delivery.firstName, customer.lastName || delivery.lastName].filter(Boolean).join(" ") || "-";
};

/**
 * Orderpicking -> Tekorten: the orders of a pick day that left the freezer short - a vak was empty
 * (VAK LEEG on the scanner) or the picker aborted the order - with what is missing and the
 * customer's phone number, and what was agreed when calling (functions/shortages). The pick day
 * lives in the URL, like on Pickrondes.
 */
const Shortages = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const pickDay = params.get("pickDay") || todayPickDay();

  const dayOrders = usePickDayOrders(pickDay);
  const { products } = useProducts();
  const { nameOf } = usePickers();
  const { saveHandling } = useShortageHandling();
  const productIndex = useMemo(() => buildProductIndex(products), [products]);
  const entries = useMemo(() => findShortages(dayOrders.orders, { productIndex }), [dayOrders.orders, productIndex]);
  const summary = summariseShortages(entries);
  const [show, setShow] = useState("open");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const visible = show === "open" ? entries.filter(isShortageOpen) : entries;
  const notLoaded = Object.keys(dayOrders.errorsByShop || {});

  const setDay = (date) => navigate(`${urls.shortages}?pickDay=${date ? date.format(DATE_FORMAT_DD_MM_YYYY) : ""}`, { replace: true });

  const openEditor = (entry) =>
    setEditing({ entry, outcome: entry.handling ? entry.handling.outcome : null, note: (entry.handling && entry.handling.note) || "" });

  const save = async () => {
    setSaving(true);
    try {
      await saveHandling(editing.entry.order, { outcome: editing.outcome, note: editing.note });
      message.success(t("orderpicking.shortages.saved", { orderId: editing.entry.order.orderId }), 3);
      setEditing(null);
    } catch (error) {
      message.error(error.message === "shortages/no-hvo-session" ? t("orderpicking.shortages.no_hvo_session") : error.message, 6);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: t("orderpicking.shortages.col_order"),
      dataIndex: "order",
      render: (order) => (
        <Space direction="vertical" size={0}>
          <strong>{order.orderId}</strong>
          <Space size={4} wrap>
            <Tag color={order.shopSource === "VCE" ? "green" : "purple"}>{order.shopSource}</Tag>
            <Tag>{order.delivery && order.delivery.type === "pickup" ? t("orderpicking.shortages.pickup") : t("orderpicking.shortages.send")}</Tag>
            {order.delivery && order.delivery.date && <Typography.Text type="secondary">{order.delivery.date}</Typography.Text>}
          </Space>
        </Space>
      ),
    },
    {
      title: t("orderpicking.shortages.col_customer"),
      dataIndex: "customer",
      render: (_, entry) => {
        const customer = entry.order.customer || {};
        return (
          <Space direction="vertical" size={0}>
            <span>{customerName(entry.order)}</span>
            {customer.phoneNumber ? (
              <a href={`tel:${customer.phoneNumber}`}>
                <PhoneOutlined /> {customer.phoneNumber}
              </a>
            ) : (
              <Typography.Text type="secondary">{t("orderpicking.shortages.no_phone")}</Typography.Text>
            )}
            {!!customer.email && <a href={`mailto:${customer.email}`}>{customer.email}</a>}
          </Space>
        );
      },
    },
    {
      title: t("orderpicking.shortages.col_missing"),
      dataIndex: "lines",
      render: (lines, entry) => (
        <Space direction="vertical" size={2}>
          {entry.aborted && <Tag color="red">{t("orderpicking.shortages.aborted")}</Tag>}
          {lines.map((line) => (
            <span key={line.articleCode}>
              <strong>{line.missing} ×</strong> {line.name}
              {line.weightLabel ? ` · ${line.weightLabel}` : ""}
              {line.emptyLocation && (
                <Tag color="orange" style={{ marginLeft: 6 }}>
                  {line.location ? t("orderpicking.shortages.empty_vak", { location: line.location }) : t("orderpicking.shortages.empty_vak_unknown")}
                </Tag>
              )}
            </span>
          ))}
        </Space>
      ),
    },
    {
      title: t("orderpicking.shortages.col_round"),
      dataIndex: "round",
      width: 150,
      render: (_, entry) => {
        const picking = entry.order.picking;
        return (
          <Space direction="vertical" size={0}>
            <Link to={`${urls.pickWaves}?pickDay=${pickDay}&wave=${picking.waveId}`}>{t("orderpicking.waves.round_number", { number: picking.waveNumber })}</Link>
            {!!picking.pickerId && <Typography.Text type="secondary">{nameOf(picking.pickerId) || picking.pickerId}</Typography.Text>}
          </Space>
        );
      },
    },
    {
      title: t("orderpicking.shortages.col_handling"),
      dataIndex: "handling",
      width: 260,
      render: (handling, entry) => (
        <Space direction="vertical" size={2}>
          {handling ? (
            <>
              <Tag color={OUTCOME_COLORS[handling.outcome]}>{t(`orderpicking.shortages.outcome_${handling.outcome}`)}</Tag>
              {!!handling.note && <span>{handling.note}</span>}
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t("orderpicking.shortages.handled_by", { who: (handling.by && handling.by.email) || "-", when: dayjs(handling.at).format("D MMM HH:mm") })}
              </Typography.Text>
            </>
          ) : (
            <Tag>{t("orderpicking.shortages.to_call")}</Tag>
          )}
          <Button size="small" onClick={() => openEditor(entry)}>
            {handling ? t("orderpicking.shortages.change") : t("orderpicking.shortages.handle")}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={t("orderpicking.shortages.title")}
      extra={
        <Space wrap>
          <DatePicker value={toDayjs(pickDay)} locale={locale} format={DATE_FORMAT_DD_MM_YYYY} allowClear={false} onChange={setDay} />
          <Button icon={<ReloadOutlined />} onClick={() => dayOrders.refresh()}>
            {t("orderpicking.shortages.refresh")}
          </Button>
        </Space>
      }
    >
      <PickDayStepsBar pickDay={pickDay} page="shortages" />
      <p style={{ maxWidth: 860, color: "#6b6b6b" }}>{t("orderpicking.shortages.explanation")}</p>
      {notLoaded.length > 0 && (
        <Alert type="warning" showIcon className="mb-3" message={t("orderpicking.shortages.not_loaded", { shops: notLoaded.join(", ") })} />
      )}
      {dayOrders.isLoading ? (
        <Loading />
      ) : (
        <>
          <Space className="mb-3" wrap>
            <Segmented
              value={show}
              onChange={setShow}
              options={[
                { value: "open", label: t("orderpicking.shortages.show_open", { count: summary.open }) },
                { value: "all", label: t("orderpicking.shortages.show_all", { count: summary.orders }) },
              ]}
            />
            {summary.orders > 0 && <Typography.Text type="secondary">{t("orderpicking.shortages.summary", summary)}</Typography.Text>}
          </Space>
          <div className="table-responsive">
            <Table
              size="small"
              rowKey="orderRef"
              columns={columns}
              dataSource={visible}
              pagination={false}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={summary.orders > 0 ? t("orderpicking.shortages.all_handled") : t("orderpicking.shortages.empty", { day: pickDay })}
                  />
                ),
              }}
            />
          </div>
        </>
      )}

      <Modal
        open={!!editing}
        title={editing ? t("orderpicking.shortages.handle_title", { orderId: editing.entry.order.orderId }) : ""}
        okText={t("orderpicking.shortages.save")}
        cancelText={t("cancel")}
        okButtonProps={{ loading: saving }}
        onOk={save}
        onCancel={() => setEditing(null)}
      >
        {!!editing && (
          <>
            <p style={{ color: "#6b6b6b" }}>{t("orderpicking.shortages.handle_help")}</p>
            <Radio.Group value={editing.outcome} onChange={(event) => setEditing({ ...editing, outcome: event.target.value })}>
              <Space direction="vertical">
                {SHORTAGE_OUTCOMES.map((outcome) => (
                  <Radio key={outcome} value={outcome}>
                    {t(`orderpicking.shortages.outcome_${outcome}`)}
                  </Radio>
                ))}
                <Radio value={null}>{t("orderpicking.shortages.to_call")}</Radio>
              </Space>
            </Radio.Group>
            <Input.TextArea
              rows={3}
              style={{ marginTop: 12 }}
              value={editing.note}
              placeholder={t("orderpicking.shortages.note_placeholder")}
              onChange={(event) => setEditing({ ...editing, note: event.target.value })}
            />
          </>
        )}
      </Modal>
    </Card>
  );
};

export default Shortages;
